import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Tag, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { AttributeTemplate, Category } from '@/types/inventory';
import { AttributePicker } from './AttributePicker';

type AttrValue = string | string[];
type AttrMap = Record<string, AttrValue>;

interface AttrDef {
  name: string;
  options?: string[];
  required?: boolean;
  multi?: boolean;
}

interface Props {
  category: Category | null | undefined;
  /**
   * Tenant-wide item attribute template — layered ON TOP of the
   * category template (category overrides on key collision).
   * Pass `[]` (or omit) if the tenant has no template yet.
   */
  tenantAttrs?: AttrDef[];
  value: AttrMap | null | undefined;
  onChange: (next: AttrMap) => void;
  /**
   * Fires when the operator picks "Create XYZ" inside an existing
   * attribute's dropdown. The parent persists this back to the
   * owning template (category for category attrs, tenant for tenant
   * attrs) so the value shows up as a suggestion on the next item.
   */
  onCreateOption?: (attrName: string, created: string) => void;
  /**
   * Fires when the operator uses "+ Add attribute" or the Quick
   * custom row to declare a brand-new attribute key. The parent
   * optimistically extends the tenant template cache so the new
   * attr renders as a normal picker for the rest of this edit
   * session. The BE auto-learns on save.
   */
  onCreateAttribute?: (attr: { name: string; options: string[]; multi: boolean }) => void;
}

const hasValue = (v: AttrValue | undefined): boolean =>
  Array.isArray(v) ? v.length > 0 : !!(v && v.trim());

/**
 * Item-level attribute editor.
 *
 *   Renders the union of two templates: the category's
 *   `attributeTemplate.itemAttributes` and the tenant's
 *   `itemAttributeTemplate.itemAttributes`. On key collision the
 *   category template wins (more specific). Required attrs are
 *   always visible; optional attrs collapse behind a "More details"
 *   toggle that auto-opens when any optional value is already set.
 *
 *   Each known attribute renders as:
 *     - single-pick with options    → searchable combobox
 *     - single-pick free-form       → text input
 *     - multi-select with options   → multi-select combobox
 *
 *   Ad-hoc entries — keys present on the item but not on either
 *   template (e.g. typed via Quick custom) — render below the
 *   templated ones as removable key/value rows.
 *
 *   Two paths to declare a new attribute key:
 *     - "+ Add attribute"  opens a dialog with name + first value +
 *                          multi toggle (structured / suggestion-friendly)
 *     - Quick custom row   always-visible key/value pair (ad-hoc)
 */
export function CategoryAttributesField({
  category,
  tenantAttrs = [],
  value,
  onChange,
  onCreateOption,
  onCreateAttribute,
}: Props) {
  const catAttrs: AttrDef[] = useMemo(
    () => category?.attributeTemplate?.itemAttributes ?? [],
    [category],
  );

  // Merge tenant + category. Category overrides on key collision.
  const merged: Array<{ def: AttrDef; scope: 'tenant' | 'category' }> = useMemo(() => {
    const byName = new Map<string, { def: AttrDef; scope: 'tenant' | 'category' }>();
    for (const a of tenantAttrs) byName.set(a.name.toLowerCase(), { def: a, scope: 'tenant' });
    for (const a of catAttrs) byName.set(a.name.toLowerCase(), { def: a, scope: 'category' });
    return Array.from(byName.values());
  }, [tenantAttrs, catAttrs]);

  const required = useMemo(() => merged.filter((m) => m.def.required), [merged]);
  const optional = useMemo(() => merged.filter((m) => !m.def.required), [merged]);

  const current: AttrMap = value ?? {};

  // Ad-hoc entries — keys in `current` that aren't on either
  // template. Surface them as editable key/value rows so the
  // operator can see + remove what they've added.
  const mergedKeys = useMemo(
    () => new Set(merged.map((m) => m.def.name.toLowerCase())),
    [merged],
  );
  const extras = useMemo(() => {
    const out: Array<{ name: string; value: AttrValue }> = [];
    for (const [k, v] of Object.entries(current)) {
      if (!mergedKeys.has(k.toLowerCase())) out.push({ name: k, value: v });
    }
    return out;
  }, [current, mergedKeys]);

  const initialExpanded =
    optional.some((m) => hasValue(current[m.def.name])) || extras.length > 0;
  const [expanded, setExpanded] = useState(initialExpanded);

  // Hide the whole panel only when there's nothing to render AND
  // no way to extend — i.e. no templates AND no onCreateAttribute.
  if (merged.length === 0 && extras.length === 0 && !onCreateAttribute) {
    return null;
  }

  const setSingle = (name: string, next: string) => {
    const updated: AttrMap = { ...current };
    if (!next) delete updated[name];
    else updated[name] = next;
    onChange(updated);
  };
  const setMulti = (name: string, next: string[]) => {
    const updated: AttrMap = { ...current };
    if (next.length === 0) delete updated[name];
    else updated[name] = next;
    onChange(updated);
  };
  const removeKey = (name: string) => {
    const updated: AttrMap = { ...current };
    delete updated[name];
    onChange(updated);
  };

  const filledOptional = optional.filter((m) => hasValue(current[m.def.name])).length;
  const filledExtras = extras.length;

  const handleNewAttr = (attr: { name: string; options: string[]; multi: boolean }) => {
    onCreateAttribute?.(attr);
    // Pre-set the first option as the value so the operator's intent
    // ("I want Country of Origin = India on this item") lands.
    if (attr.options.length > 0) {
      const updated: AttrMap = { ...current };
      updated[attr.name] = attr.multi ? [...attr.options] : attr.options[0];
      onChange(updated);
    }
    setExpanded(true);
  };

  return (
    <div className="rounded-md border border-primary/25 bg-primary/[0.03] px-3.5 py-3">
      <div className="mb-2.5 flex items-center gap-2">
        <span className="grid h-6 w-6 place-items-center rounded-md bg-primary/15 text-primary">
          <Tag className="h-3.5 w-3.5" />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold text-foreground">
            Product details
          </div>
          <div className="text-[11px] text-muted-foreground">
            What kind of {category?.name?.toLowerCase() ?? 'item'} is this?
            These show on the storefront and help customers filter. Type a new
            value in any dropdown to add it, or use{' '}
            <span className="font-medium text-foreground">+ Add attribute</span>{' '}
            below to introduce a new field.
          </div>
        </div>
      </div>

      {required.length > 0 && (
        <div className="grid gap-2.5 sm:grid-cols-2">
          {required.map(({ def }) => (
            <AttrControl
              key={def.name}
              attr={def}
              value={current[def.name]}
              onSingle={(v) => setSingle(def.name, v)}
              onMulti={(v) => setMulti(def.name, v)}
              onCreate={
                onCreateOption ? (v) => onCreateOption(def.name, v) : undefined
              }
            />
          ))}
        </div>
      )}

      {(optional.length > 0 || extras.length > 0 || onCreateAttribute) && (
        <>
          {required.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-2.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-primary hover:bg-primary/10"
            >
              {expanded ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {expanded ? 'Hide' : 'More details'}
              {!expanded && (filledOptional > 0 || filledExtras > 0) && (
                <span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">
                  {filledOptional + filledExtras}
                </span>
              )}
              {!expanded && filledOptional + filledExtras === 0 && (
                <span className="text-[10.5px] font-normal text-muted-foreground">
                  ({optional.length} optional)
                </span>
              )}
            </button>
          )}
          {(expanded || required.length === 0) && (
            <>
              {optional.length > 0 && (
                <div
                  className={`grid gap-2.5 sm:grid-cols-2 ${
                    required.length > 0 ? 'mt-2.5' : ''
                  }`}
                >
                  {optional.map(({ def }) => (
                    <AttrControl
                      key={def.name}
                      attr={def}
                      value={current[def.name]}
                      onSingle={(v) => setSingle(def.name, v)}
                      onMulti={(v) => setMulti(def.name, v)}
                      onCreate={
                        onCreateOption
                          ? (v) => onCreateOption(def.name, v)
                          : undefined
                      }
                      clearable
                    />
                  ))}
                </div>
              )}

              {extras.length > 0 && (
                <div className="mt-2.5 space-y-1.5 border-t border-primary/20 pt-2.5">
                  <div className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
                    Custom fields
                  </div>
                  {extras.map((row) => (
                    <ExtraRow
                      key={row.name}
                      name={row.name}
                      value={row.value}
                      onChange={(v) => setSingle(row.name, v)}
                      onRemove={() => removeKey(row.name)}
                    />
                  ))}
                </div>
              )}

              {onCreateAttribute && (
                <NewAttributeRow
                  existingNames={mergedKeys}
                  onAddStructured={handleNewAttr}
                  onAddQuick={(name, value) => {
                    // Quick custom — record value on item AND ask the
                    // parent to declare a new tenant-scope attr so the
                    // key gets remembered. Free-form (no options).
                    handleNewAttr({ name, options: value ? [value] : [], multi: false });
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────── primitives ────────────────────────────

function AttrControl({
  attr,
  value,
  onSingle,
  onMulti,
  onCreate,
  clearable = false,
}: {
  attr: AttrDef;
  value: AttrValue | undefined;
  onSingle: (v: string) => void;
  onMulti: (v: string[]) => void;
  onCreate?: (v: string) => void;
  clearable?: boolean;
}) {
  const options = attr.options ?? [];
  const hasOptions = options.length > 0;

  if (!hasOptions) {
    const single = typeof value === 'string' ? value : '';
    return (
      <div className="space-y-1">
        <Label attr={attr} />
        <Input
          type="text"
          value={single}
          onChange={(e) => onSingle(e.target.value)}
          placeholder="Type a value"
          className="h-9 text-[13px]"
        />
      </div>
    );
  }

  if (attr.multi) {
    const arr = Array.isArray(value) ? value : value ? [value] : [];
    return (
      <div className="space-y-1 sm:col-span-2">
        <Label attr={attr} hint="pick one or more" />
        <AttributePicker
          multi
          options={options}
          value={arr}
          onChange={onMulti}
          onCreate={onCreate}
          placeholder="Select…"
          required={attr.required}
          ariaLabel={attr.name}
        />
      </div>
    );
  }

  const single = typeof value === 'string' ? value : '';
  return (
    <div className="space-y-1">
      <Label attr={attr} />
      <AttributePicker
        options={options}
        value={single}
        onChange={onSingle}
        onCreate={onCreate}
        placeholder={attr.required ? `Pick ${attr.name.toLowerCase()}` : 'Select'}
        required={attr.required}
        ariaLabel={attr.name}
      />
      {clearable && single && (
        <button
          type="button"
          onClick={() => onSingle('')}
          className="text-[10.5px] text-muted-foreground hover:text-destructive"
        >
          Clear
        </button>
      )}
    </div>
  );
}

function Label({ attr, hint }: { attr: AttrDef; hint?: string }) {
  return (
    <div className="flex items-center gap-1">
      <label className="text-[11.5px] font-medium text-foreground">
        {attr.name}
        {attr.required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {hint && (
        <span className="text-[10.5px] text-muted-foreground">({hint})</span>
      )}
    </div>
  );
}

function ExtraRow({
  name,
  value,
  onChange,
  onRemove,
}: {
  name: string;
  value: AttrValue;
  onChange: (v: string) => void;
  onRemove: () => void;
}) {
  const single = typeof value === 'string' ? value : (value ?? []).join(', ');
  return (
    <div className="flex items-center gap-2">
      <div className="w-1/3 truncate text-[12px] font-medium text-foreground">
        {name}
      </div>
      <Input
        value={single}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 flex-1 text-[12.5px]"
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${name}`}
        className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─────────────────────── new-attribute entry points ───────────────────────

function NewAttributeRow({
  existingNames,
  onAddStructured,
  onAddQuick,
}: {
  existingNames: Set<string>;
  onAddStructured: (attr: { name: string; options: string[]; multi: boolean }) => void;
  onAddQuick: (name: string, value: string) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickKey, setQuickKey] = useState('');
  const [quickVal, setQuickVal] = useState('');

  const submitQuick = () => {
    const k = quickKey.trim();
    const v = quickVal.trim();
    if (!k) return;
    if (existingNames.has(k.toLowerCase())) {
      // Already defined — just set the value on the existing attr.
      // Caller does the right thing because onAddQuick → handleNewAttr,
      // which would re-add the attr. Guard at this entry point.
      // The operator can use the regular picker for known attrs.
      return;
    }
    onAddQuick(k, v);
    setQuickKey('');
    setQuickVal('');
  };

  return (
    <div className="mt-2.5 space-y-2 border-t border-primary/20 pt-2.5">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
          Add a new field
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setDialogOpen(true)}
          className="h-7 px-2 text-[11.5px]"
        >
          <Plus className="mr-1 h-3 w-3" />
          Add attribute
        </Button>
      </div>
      <div className="flex items-center gap-2 text-[12px]">
        <span className="shrink-0 text-muted-foreground">Quick:</span>
        <Input
          value={quickKey}
          onChange={(e) => setQuickKey(e.target.value)}
          placeholder="Field name"
          className="h-8 w-1/3 text-[12.5px]"
        />
        <Input
          value={quickVal}
          onChange={(e) => setQuickVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitQuick();
            }
          }}
          placeholder="Value"
          className="h-8 flex-1 text-[12.5px]"
        />
        <Button
          type="button"
          size="sm"
          onClick={submitQuick}
          disabled={!quickKey.trim()}
          className="h-8 px-2 text-[11.5px]"
        >
          Add
        </Button>
      </div>
      <NewAttributeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        existingNames={existingNames}
        onConfirm={(attr) => {
          onAddStructured(attr);
          setDialogOpen(false);
        }}
      />
    </div>
  );
}

function NewAttributeDialog({
  open,
  onOpenChange,
  existingNames,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existingNames: Set<string>;
  onConfirm: (attr: { name: string; options: string[]; multi: boolean }) => void;
}) {
  const [name, setName] = useState('');
  const [optionsRaw, setOptionsRaw] = useState('');
  const [multi, setMulti] = useState(false);

  const reset = () => {
    setName('');
    setOptionsRaw('');
    setMulti(false);
  };

  const nameClean = name.trim();
  const collision =
    !!nameClean && existingNames.has(nameClean.toLowerCase());
  const options = optionsRaw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a custom attribute</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <p className="text-muted-foreground">
            Defines a new field for this and future items. It&apos;ll show up as
            a regular dropdown — and any value you set here becomes a suggestion
            on the next item.
          </p>
          <div className="space-y-1">
            <label className="text-[11.5px] font-medium text-foreground">
              Field name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Country of Origin"
              autoFocus
            />
            {collision && (
              <p className="text-[11px] text-destructive">
                A field named &ldquo;{nameClean}&rdquo; already exists. Use the
                existing dropdown to set its value.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-[11.5px] font-medium text-foreground">
              Suggested values
              <span className="ml-1 font-normal text-muted-foreground">
                (comma-separated, optional)
              </span>
            </label>
            <Input
              value={optionsRaw}
              onChange={(e) => setOptionsRaw(e.target.value)}
              placeholder="India, China, Vietnam"
            />
            <p className="text-[10.5px] text-muted-foreground">
              Operators can also type new values into the dropdown later — the
              list grows over time.
            </p>
          </div>
          <label className="flex items-center gap-2 text-[12.5px]">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={multi}
              onChange={(e) => setMulti(e.target.checked)}
            />
            <span>Allow multiple values per item (multi-select)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            type="button"
          >
            <X className="mr-1 h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            onClick={() => onConfirm({ name: nameClean, options, multi })}
            disabled={!nameClean || collision}
            type="button"
          >
            Add field
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// keep the AttributeTemplate type referenced so a future-self
// can re-import without confusion.
export type { AttributeTemplate };
